import { type PropsWithChildren, type ReactElement, useEffect, useMemo } from 'react';
import { createContext, useContext } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import {
  GET_FULLY_POPULATED_SUB_EVENT,
  type GetFullyPopulatedSubEventData,
  type GetFullyPopulatedSubEventVariables,
} from './graphql/getFullyPopulatedSubEvent.ts';
import GeneralError from '../views/error/GeneralError.tsx';
import type { KarwaUrlParams } from '../views/types.ts';
import { AWARDS_SELECT_VALUE_ALL, AWARDS_SELECT_VALUE_MULTI } from '../views/awards/constants.ts';
import { useApp } from './AppProvider.tsx';
import { useTranslation } from 'react-i18next';
import LeaderboardEmptyState from '../views/leaderboard/components/LeaderboardEmptyState.tsx';
import PrivateResultsBanner from '../views/leaderboard/components/PrivateResultsBanner.tsx';
import { useSession } from './SessionProvider.tsx';
import type { FullSubEvent } from '@fastnorth/timer-api';
import logDebugMessage from '../utils/logDebugMessage.ts';
import ContextualLoadingSkeleton from './components/ContextualLoadingSkeleton.tsx';
import { Div, Flex } from '@fastnorth/kraken/components/layout';
import { DEFAULT_SUB_EVENT } from '../views/leaderboard/components/details/hooks/mocks';

export type SubEventProviderProps = {
  sampleMode?: boolean;
};

export type SubEventState = {
  subEvent?: FullSubEvent;
};

export const defaultState: SubEventState = {
  subEvent: undefined,
};

type SubEventProviderCtx = SubEventState;

const SubEventCtx = createContext<SubEventProviderCtx>(defaultState);

const SubEventProvider = ({
  children,
  sampleMode,
}: PropsWithChildren<SubEventProviderProps>): ReactElement => {
  const { t } = useTranslation('translation', { keyPrefix: 'karwa.components.provider' });
  const [{ event }] = useApp();
  const [{ isTimer, isSuperAdmin }] = useSession();
  const { raceId = '' } = useParams<KarwaUrlParams>();

  const [fetchSubEvent, { loading, error, data, called }] = useLazyQuery<
    GetFullyPopulatedSubEventData,
    GetFullyPopulatedSubEventVariables
  >(GET_FULLY_POPULATED_SUB_EVENT, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (
      event?.uniqueCode &&
      raceId &&
      [AWARDS_SELECT_VALUE_ALL, AWARDS_SELECT_VALUE_MULTI].indexOf(raceId) === -1 &&
      !sampleMode
    ) {
      fetchSubEvent({
        variables: {
          eventUniqueCode: event?.uniqueCode,
          subEventId: raceId,
        },
      });
    }
  }, [raceId, event?.uniqueCode, fetchSubEvent, sampleMode]);

  const subEvent = data?.getFullyPopulatedSubEvent;

  const shouldBypassLoading = useMemo(() => {
    return [AWARDS_SELECT_VALUE_ALL, AWARDS_SELECT_VALUE_MULTI].indexOf(raceId) >= 0;
  }, [raceId]);

  if (sampleMode) {
    logDebugMessage('[SubEventProvider] rendering children in sample mode');
    return (
      <SubEventCtx.Provider value={{ subEvent: DEFAULT_SUB_EVENT }}>
        {children}
      </SubEventCtx.Provider>
    );
  }

  if (error) {
    logDebugMessage('[SubEventProvider] rendering Error');
    return (
      <GeneralError
        id={'sub-event-provider-error'}
        title={t('error_state.title', { itemName: 'race' })}
        message={t('error_state.message', { itemName: 'race' })}
        error={error}
      />
    );
  }

  // if we are bypassing, we won't really need subEvent data
  if (shouldBypassLoading) {
    logDebugMessage('[SubEventProvider] rendering children without subEvent');
    return (
      <SubEventCtx.Provider value={{ subEvent: {} as FullSubEvent }}>
        {children}
      </SubEventCtx.Provider>
    );
  }

  if (!called || (loading && !data)) {
    logDebugMessage('[SubEventProvider] rendering skeleton', { raceId });
    //show loading
    return (
      <Div data-testid={'sub-event-skeleton'}>
        <ContextualLoadingSkeleton />
      </Div>
    );
  }

  if (!loading && called && (!data || !data.getFullyPopulatedSubEvent)) {
    logDebugMessage('[SubEventProvider] rendering Empty state');
    return <LeaderboardEmptyState />;
  }

  if (!loading && called && data?.getFullyPopulatedSubEvent?.isPublic === false) {
    logDebugMessage('[SubEventProvider] rendering private state');
    // Only event directors (isTimer) and super admins can preview private results.
    // All other users — including authenticated non-directors — see the same empty
    // state as a missing race, so the privacy status is not disclosed.
    if (!(isTimer || isSuperAdmin)) {
      return <LeaderboardEmptyState />;
    }

    return (
      <SubEventCtx.Provider value={{ subEvent: subEvent! }}>
        <Flex className={'flex-col gap-4'}>
          <PrivateResultsBanner />
          {children}
        </Flex>
      </SubEventCtx.Provider>
    );
  }

  logDebugMessage('[SubEventProvider] rendering children');
  return <SubEventCtx.Provider value={{ subEvent: subEvent! }}>{children}</SubEventCtx.Provider>;
};

export const useSubEvent = (): SubEventProviderCtx => useContext<SubEventProviderCtx>(SubEventCtx);

export default SubEventProvider;
